import React, { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';
import { B2BContactForm } from '../components/B2BContactForm.js';

interface BusinessPageProps {
  isEn?: boolean;
}

export const BusinessPage: React.FC<BusinessPageProps> = ({ isEn = false }) => {
  const c = isEn ? contentEn : contentEs;
  const b = c.business;

  useEffect(() => {
    document.title = c.meta.business.title;
  }, [c.meta.business.title]);

  const stepImages = [
    '/website/abastecimiento.png',
    '/website/produccion.png',
    '/website/cocina.png',
    '/website/panaderia.png',
    '/website/distribucion.png',
    '/website/recepcion.png',
  ];

  return (
    <div className="flex flex-col bg-[#FBF9F5]">
      <section className="relative bg-[#001557] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <div className="lg:col-span-7 space-y-8">
              <div>
                <span className="text-xs uppercase font-extrabold tracking-widest text-[#93C5FD]">
                  {b.hero.eyebrow}
                </span>
                <h1 className="mt-3 font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-tight">
                  <span>{b.hero.title} </span>
                  <span className="text-[#38BDF8]">{b.hero.titleAccent}</span>
                </h1>
                <p className="mt-6 text-base sm:text-lg text-[#BFDBFE] leading-relaxed max-w-2xl">
                  {b.hero.body}
                </p>
              </div>

              <div className="space-y-6 pt-4 border-t border-white/10">
                {b.solutions.map((sol) => (
                  <div key={sol.num} className="flex items-start gap-5 group">
                    <span className="font-heading font-black text-2xl sm:text-3xl text-[#38BDF8] shrink-0 w-10">
                      {sol.num}
                    </span>
                    <div>
                      <h3 className="font-heading font-bold text-lg sm:text-xl text-white">
                        {sol.title}
                      </h3>
                      <p className="mt-1 text-sm text-[#BFDBFE] leading-relaxed">
                        {sol.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <a
                  href="#contacto"
                  className="inline-flex items-center gap-2 bg-white text-[#001557] hover:bg-gray-100 font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  <span>{b.hero.cta}</span>
                  <ArrowRight className="w-5 h-5 text-[#001557]" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 h-full flex items-center justify-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 w-full max-h-[820px] aspect-[4/5] lg:aspect-auto">
                <img
                  src="/website/b2b-hero-chef.jpg"
                  alt="Equipo de cocina Doña Blanca"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001557]/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 p-4 rounded-xl bg-black/40 backdrop-blur-md border border-white/20 text-white">
                  <p className="font-heading font-bold text-sm">
                    {isEn ? 'Culinary excellence & institutional hygiene' : 'Inocuidad alimentaria y disciplina en cada servicio'}
                  </p>
                  <p className="text-xs text-gray-300 mt-0.5">
                    {isEn ? 'Daily menus crafted by certified chefs' : 'Menús nutritivos preparados por personal calificado'}
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section id="operacion" className="py-20 lg:py-28 bg-[#FAF8F5] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl">
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-[#001557] tracking-tight leading-tight">
              <span>{b.operation.title} </span>
              <span className="text-[#0284C7]">{b.operation.titleAccent}</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
              {b.operation.body}
            </p>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {b.operation.steps.map((step, idx) => (
              <div
                key={step.num}
                className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden flex flex-col shadow-sm hover:shadow-lg hover:border-[#001557]/30 transition-all duration-300 group"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-amber-50/40 border-b border-[#F0ECE4]">
                  <img
                    src={stepImages[idx]}
                    alt={step.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-2.5 left-2.5">
                    <span className="font-heading font-black text-xs text-[#001557] bg-white/95 px-2 py-0.5 rounded-md shadow-sm border border-[#E5E0D8]">
                      {step.num}
                    </span>
                  </div>
                </div>
                <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <h4 className="font-heading font-bold text-sm sm:text-base text-gray-900 leading-snug group-hover:text-[#001557] transition-colors">
                      {step.name}
                    </h4>
                    <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#001557] tracking-tight">
              {b.production.title}
            </h2>
            <p className="mt-4 text-base text-gray-600 leading-relaxed">
              {b.production.body}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {b.production.highlights.map((h) => (
              <div key={h.title} className="p-6 rounded-xl bg-[#FAF8F5] border border-[#E5E0D8]">
                <h4 className="font-heading font-black text-sm text-[#001557] tracking-wider uppercase">
                  {h.title}
                </h4>
                <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                  {h.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-20 pt-16 border-t border-gray-100 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 rounded-2xl overflow-hidden shadow-lg border border-[#E5E0D8]">
              <img
                src="/website/panaderia.png"
                alt="Panadería propia y horneo diario Doña Blanca"
                className="w-full h-80 sm:h-96 object-cover"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-6 space-y-4 lg:pl-6">
              <span className="text-xs uppercase font-extrabold tracking-widest text-[#97472E]">
                {b.bakery.eyebrow}
              </span>
              <h3 className="font-heading font-black text-2xl sm:text-4xl text-[#001557] leading-tight">
                <span>{b.bakery.title} </span>
                <span className="text-[#0284C7]">{b.bakery.titleAccent}</span>
              </h3>
              <p className="text-base text-gray-600 leading-relaxed pt-2">
                {b.bakery.body}
              </p>
              <p className="text-xs text-gray-500 font-medium italic pt-2">
                {b.production.closing}
              </p>
            </div>
          </div>

        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#001557] text-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
            {b.flexibility.title}
          </h2>
          
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {b.flexibility.factors.map((f) => (
              <span
                key={f}
                className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs font-bold text-sky-200 shadow-sm uppercase tracking-wider backdrop-blur-sm"
              >
                {f}
              </span>
            ))}
          </div>

          <p className="mt-8 text-base sm:text-lg text-[#BFDBFE] max-w-2xl mx-auto leading-relaxed">
            {b.flexibility.body}
          </p>

          <div className="mt-10">
            <a
              href="#contacto"
              className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#D99B00] text-[#001557] font-bold text-base px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-[#CA8A04]/50 hover:scale-[1.02]"
            >
              <span>{b.flexibility.cta}</span>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <h2 className="font-heading font-black text-3xl sm:text-5xl text-[#001557] tracking-tight leading-tight">
                <span>{b.differentiator.title} </span>
                <span className="text-[#0284C7]">{b.differentiator.titleAccent}</span>
              </h2>
              <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                {b.differentiator.body}
              </p>
            </div>

            <div className="lg:col-span-5 rounded-2xl overflow-hidden shadow-md border border-[#E5E0D8]">
              <img
                src="/website/produccion.png"
                alt="Producción y estandarización Doña Blanca"
                className="w-full h-56 sm:h-64 object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {b.differentiator.pillars.map((pil) => (
              <div key={pil.title} className="p-6 rounded-xl bg-[#FAF8F5] border border-[#E5E0D8]">
                <h4 className="font-heading font-black text-base text-[#001557]">
                  {pil.title}
                </h4>
                <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                  {pil.desc}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      <B2BContactForm isEn={isEn} />

    </div>
  );
};
